import { Button, Drawer } from "@components";
import styles from "@styles/pages/cdp/targeting-guide.module.scss";
import classNames from "classnames/bind";
import { useEffect, useState } from "react";
import { If } from "shared/components/control-statements";
import Conditions from "./Conditions";
import Filters from "./Filters";
const css = classNames.bind(styles);

const TABS = {
  SEGMENTATIONS: "SEGMENTATIONS",
  FILTERS: "FILTERS",
};

export const groupSegments = (segments, appliedSegments) => {
  const inApplied = {};
  const notInApplied = {};

  Object.entries(segments).forEach(([key, value]) => {
    if (appliedSegments[key]) {
      // Segmentos que estão em appliedSegments
      const appliedOptions = value.options.filter((option) =>
        appliedSegments[key].options.some(
          (appliedOption) => appliedOption.id === option.id
        )
      );

      if (appliedOptions.length > 0) {
        inApplied[key] = {
          ...value,
          options: appliedOptions,
        };
      }

      // Segmentos que não estão em appliedSegments
      const remainingOptions = value.options.filter(
        (option) =>
          !appliedSegments[key].options.some(
            (appliedOption) => appliedOption.id === option.id
          )
      );

      if (remainingOptions.length > 0) {
        notInApplied[key] = {
          ...value,
          options: remainingOptions,
        };
      }
    } else {
      notInApplied[key] = value;
    }
  });

  return { inApplied, notInApplied };
};

export const getSegments = (segments) => {
  const conditions = Object.entries(segments).map(([key, value]) => ({
    name: key,
    ...value,
  }));

  const flattened = conditions.flatMap((group) => {
    return (group.options || []).map((option) => ({
      ...option,
      key: group.name,
      modifier: group.modifier,
    }));
  });

  return flattened;
};

export default function TargetingGuide({
  selected_segments = {},
  selected_filters = {},
  readonly,
  appliedSegments,
  appliedFilters,
  onSegment,
  onExecute,
  onClear,
  onRemoveCondition,
  onRemoveFilter,
}) {
  const [activeTab, setActiveTab] = useState(TABS.SEGMENTATIONS);
  const [collapsed, setCollapsed] = useState(false);
  const [openClear, setOpenClear] = useState(false);

  const totalSegmentations = Object.values(selected_segments).reduce(
    (acc, arr) => acc + arr.options?.length || 0,
    0
  );

  useEffect(() => {
    if (totalSegmentations > 0 || selected_filters?.length > 0) setCollapsed(true);
    if (totalSegmentations === 0 && selected_filters?.length === 0) setCollapsed(false);

    if (selected_filters?.length > 0 && totalSegmentations === 0)
      return setActiveTab(TABS.FILTERS);

    if (totalSegmentations > 0 && selected_filters?.length === 0)
      return setActiveTab(TABS.SEGMENTATIONS);
  }, [selected_filters, totalSegmentations]);

  const { inApplied, notInApplied } = groupSegments(
    selected_segments,
    appliedSegments
  );

  const applied_segments = getSegments(inApplied);
  const not_applied_segments = getSegments(notInApplied);

  const getNewFilters = () => {
    const filters = (appliedFilters || []).map((item) => item.type);
    return (selected_filters || []).filter(
      (item) => !filters.includes(item.type)
    );
  };

  const newFilters = getNewFilters();

  const totalFilters = selected_filters?.length - newFilters?.length || 0;

  const Tabs = () => {
    if (totalSegmentations === 0 && selected_filters?.length === 0) return null;

    return (
      <div className={styles.tabsContainer}>
        <div
          onClick={() => setActiveTab(TABS.SEGMENTATIONS)}
          className={css({
            tab: true,
            active: activeTab === TABS.SEGMENTATIONS,
            inactive: activeTab !== TABS.SEGMENTATIONS,
          })}
        >
          Segmentações ({totalSegmentations})
        </div>
        <div
          onClick={() => setActiveTab(TABS.FILTERS)}
          className={css({
            tab: true,
            active: activeTab === TABS.FILTERS,
            inactive: activeTab !== TABS.FILTERS,
          })}
        >
          Filtros ({selected_filters?.length})
        </div>
      </div>
    );
  };

  return (
    <div
      className={css({
        targetingGuide: true,
        collapsed: collapsed,
        hasExecute: totalSegmentations > 0 || selected_filters?.length > 0,
      })}
    >
      <div
        className={css({ retract: true, active: collapsed })}
        onClick={() => setCollapsed(!collapsed)}
      >
        <i className="fas fa-chevron-left" />
      </div>

      <div className={styles.container}>
        <If cond={!totalSegmentations && selected_filters?.length === 0}>
          <div className={styles.empty}>
            <i className="far fa-bars-filter" />
            <p>Guia de segmentação</p>
            <span>Sua triagem de públicos irá aparecer por aqui.</span>
            <If cond={readonly === true}>
              <Button design="primary-mini" onClick={onSegment}>
                Segmentar
              </Button>
            </If>
          </div>
        </If>
        <If cond={!!totalSegmentations || selected_filters?.length > 0}>
          <div className={styles.content}>
            <div className={styles.header}>
              <p className={styles.title}>Guia de segmentação</p>
              <Button design="underline" onClick={() => setOpenClear(true)}>
                LIMPAR TUDO
              </Button>
            </div>

            {activeTab === TABS.SEGMENTATIONS && (
              <Conditions
                selected_segments={selected_segments}
                appliedSegments={appliedSegments}
                onRemove={(condition) => onRemoveCondition(condition)}
              />
            )}
            {activeTab === TABS.FILTERS && (
              <Filters
                selected_filters={selected_filters}
                appliedFilters={appliedFilters}
                onRemove={(filter) => onRemoveFilter(filter)}
              />
            )}

            <div className={styles.footer}>
              <Tabs />
            </div>
          </div>

          <div className={styles.contentMinified}>
            {not_applied_segments?.length > 0 && (
              <div className={styles.newConditionsMinified}>
                {not_applied_segments.length}
              </div>
            )}
            {newFilters?.length > 0 && (
              <div className={styles.newFiltersMinified}>
                {newFilters.length}
              </div>
            )}

            {(not_applied_segments?.length > 0 || totalFilters > 0) &&
              (applied_segments?.length > 0 || totalFilters > 0) ? (
              <div className={styles.divisor} />
            ) : null}

            {applied_segments?.length > 0 ? (
              <div className={styles.conditionsMinified}>
                {applied_segments?.length}
              </div>
            ) : null}
            {totalFilters > 0 ? (
              <div className={styles.filtersMinified}>{totalFilters}</div>
            ) : null}
          </div>
        </If>
      </div>

      <If cond={readonly === false && (totalSegmentations > 0 || selected_filters?.length > 0)}>
        <div className={styles.execute}>
          <Button design="primary" className="w-100" onClick={onExecute}>
            {collapsed && <span>Executar</span>}
            {!collapsed && <i className="fas fa-play" />}
          </Button>
        </div>
      </If>

      <Drawer
        visible={openClear}
        onClose={() => setOpenClear(false)}
        direction="center"
        title="Atenção"
        showCloseButton={false}
      >
        <div className={styles.clear}>
          <p className={styles.message}>
            Você tem certeza que deseja limpar toda a triagem de públicos?
          </p>

          <div className={styles.actions}>
            <Button
              design="primary"
              onClick={() => {
                onClear();
                setOpenClear(false);
              }}
              className="w-100"
            >
              Limpar tudo
            </Button>
            <Button className="w-100" onClick={() => setOpenClear(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      </Drawer>
    </div>
  );
}
